import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import MatrixTree from './MatrixTree';
import { useContract } from '../hooks/useContract';
import { formatAddress, formatUSDT } from '../utils/constants';

const Team = ({ account, provider, signer, network }) => {
  const { 
    contract,
    loading,
    userData,
    getUserId,
    getUserDetails,
    getUserReferrals,
    getMatrixData
  } = useContract(provider, signer, network);
  
  // User data
  const [userId, setUserId] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  
  // Team data
  const [referrals, setReferrals] = useState([]);
  const [matrixData, setMatrixData] = useState({});
  const [activities, setActivities] = useState([]);
  const [levelUsers, setLevelUsers] = useState([]);
  const [levelCounts, setLevelCounts] = useState(Array(10).fill(0));
  
  // UI state
  const [activeLevel, setActiveLevel] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [levelUsersPage, setLevelUsersPage] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(1);
  const [showLevelUsers, setShowLevelUsers] = useState(false);
  const [selectedLevelForUsers, setSelectedLevelForUsers] = useState(1);
  const [loadingLevelUsers, setLoadingLevelUsers] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);

  // LOOP PREVENTION
  const hasLoadedUserRef = useRef(false);
  const hasLoadedTeamRef = useRef(false);
  const lastAccountRef = useRef('');
  const lastPackageRef = useRef(1);

  // Load user data when account changes - ONCE ONLY
  useEffect(() => {
    // Prevent loops and duplicate calls
    if (!account || 
        !contract || 
        account === lastAccountRef.current && hasLoadedUserRef.current ||
        typeof account !== 'string') {
      return;
    }

    console.log('Loading user data for:', account);
    lastAccountRef.current = account;
    hasLoadedUserRef.current = true;
    
    // Reset team data state when account changes
    hasLoadedTeamRef.current = false;
    
    loadUserData();
  }, [account, contract]); // MINIMAL dependencies

  // Load team data when userId or selectedPackage changes - ONCE ONLY
  useEffect(() => {
    // Prevent loops and duplicate calls
    if (!userId || 
        !contract || 
        hasLoadedTeamRef.current && 
        selectedPackage === lastPackageRef.current) {
      return;
    }

    console.log('Loading team data for user:', userId, 'package:', selectedPackage);
    lastPackageRef.current = selectedPackage;
    hasLoadedTeamRef.current = true;
    
    loadTeamData();
  }, [userId, selectedPackage]); // MINIMAL dependencies

  const loadUserData = async () => {
    if (teamLoading) return; // Prevent concurrent calls
    
    setTeamLoading(true);
    try {
      // Get user ID
      const userIdResult = await getUserId(account);
      
      if (!userIdResult || userIdResult === '0') {
        console.log('User not registered');
        setUserId('');
        setUserDetails(null);
        return;
      }
      
      // Get user details
      const details = await getUserDetails(userIdResult);
      
      setUserId(userIdResult);
      setUserDetails(details);
      console.log('User data loaded successfully');
      
    } catch (error) {
      console.error('Error loading user data:', error);
      setUserId('');
      setUserDetails(null);
    } finally {
      setTeamLoading(false);
    }
  };

  const loadTeamData = async () => {
    if (teamLoading) return; // Prevent concurrent calls
    
    try {
      // Load all data in parallel with SafePal support
      const [
        referralData,
        matrixDetails,
        levelCountsData,
        activitiesData
      ] = await Promise.all([
        getUserReferrals(userId).catch(() => []),
        getMatrixData(userId, selectedPackage).catch(() => ({ upline: '0', directDownlines: [], totalTeamSize: '0' })),
        // Level counts - optional
        contract?.getMatrixLevelCounts ? 
          contract.getMatrixLevelCounts(userId, selectedPackage).catch(() => Array(10).fill(0)) : 
          Array(10).fill(0),
        // Activities - optional
        contract?.getUserActivities ? 
          contract.getUserActivities(userId).catch(() => []) : 
          []
      ]);

      // Process matrix data
      setMatrixData(matrixDetails);

      // Process level counts
      const countsArray = Array.isArray(levelCountsData) ? 
        levelCountsData.map(count => count.toString()) : 
        Array(10).fill('0');
      setLevelCounts(countsArray);

      // Process activities
      const sortedActivities = Array.isArray(activitiesData) ? 
        activitiesData.map(activity => ({
          id: activity.id?.toString() || '',
          userId: activity.userId?.toString() || '',
          activityType: activity.activityType?.toString() || '',
          amount: activity.amount ? ethers.utils.formatEther(activity.amount) : '0',
          packageId: activity.packageId?.toString() || '',
          fromId: activity.fromId?.toString() || '',
          timestamp: activity.timestamp?.toString() || ''
        })).sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp)) : [];
      setActivities(sortedActivities);

      // Set referrals
      setReferrals(referralData || []);

      console.log('Team data loaded successfully');
      
    } catch (error) {
      console.error('Error loading team data:', error);
      setReferrals([]);
      setMatrixData({ upline: '0', directDownlines: [], totalTeamSize: '0' });
      setActivities([]);
      setLevelCounts(Array(10).fill('0'));
    }
  };

  // Load level users - NO LOOP ISSUES
  const loadLevelUsers = async (level) => {
    if (!userId || !contract) return;

    setLoadingLevelUsers(true);
    setShowLevelUsers(true);
    setSelectedLevelForUsers(level);
    setLevelUsersPage(1);

    try {
      if (contract.getMatrixLevelUsers) {
        const levelUserIds = await contract.getMatrixLevelUsers(userId, selectedPackage, level);
        const usersList = levelUserIds.map(id => ({
          id: id.toString()
        })).filter(user => user.id !== '0');

        setLevelUsers(usersList);
      } else {
        setLevelUsers([]);
      }
    } catch (error) {
      console.error('Error loading level users:', error);
      setLevelUsers([]);
    } finally {
      setLoadingLevelUsers(false);
    }
  };

  // Activity helpers
  const getActivityTypeLabel = (type) => {
    const types = {
      '0': 'Registration',
      '1': 'Package Upgrade', 
      '2': 'Sponsor Income',
      '3': 'Level Income',
      '4': 'Pool Income',
      '5': 'Income Claimed',
      '6': 'Package Retopup'
    };
    return types[type] || `Activity ${type}`;
  };

  const getActivityIcon = (type) => {
    const icons = {
      '0': '👤', '1': '📈', '2': '💰', '3': '🎯', '4': '🏊', '5': '💸', '6': '🔄'
    };
    return icons[type] || '📊';
  };

  // Event handlers - NO LOOPS
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleLevelClick = (level) => { 
    setActiveLevel(level); 
    setCurrentPage(1);
    if (level === 1) {
      setShowLevelUsers(false);
    }
  };

  const handlePackageChange = (e) => {
    const newPackage = parseInt(e.target.value);
    setSelectedPackage(newPackage);
    setShowLevelUsers(false);
    // Force reload team data for new package
    hasLoadedTeamRef.current = false;
  };

  // Pagination calculations
  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReferrals = referrals.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(referrals.length / itemsPerPage);

  // Level users pagination
  const levelUsersIndexOfLast = levelUsersPage * itemsPerPage;
  const levelUsersIndexOfFirst = levelUsersIndexOfLast - itemsPerPage;
  const currentLevelUsers = levelUsers.slice(levelUsersIndexOfFirst, levelUsersIndexOfLast);
  const levelUsersTotalPages = Math.ceil(levelUsers.length / itemsPerPage);

  const teamActiveMembers = referrals.filter(r => r.isActive).length;

  if (loading || teamLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading team data...</p>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="team">
        <div className="no-data-message">
          <h3>User Not Registered</h3>
          <p>Please register first to view your team data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team">
      {/* Team Statistics */}
      <section className="team-stats-section">
        <div className="team-stats-grid">
          <div className="stat-card">
            <h4>Direct Referrals</h4>
            <span className="stat-value">{userDetails?.totalDirectReferrals || userData?.totalDirectReferrals || '0'}</span>
          </div>
          <div className="stat-card">
            <h4>Total Team</h4>
            <span className="stat-value">{matrixData?.totalTeamSize || '0'}</span>
          </div>
          <div className="stat-card">
            <h4>Active Members</h4>
            <span className="stat-value">{teamActiveMembers}</span>
          </div>
        </div>
      </section>

      {/* Level Breakdown */}
      <section className="levels-section">
        <div className="levels-header">
          <h3>Team Level Distribution</h3>
          <div className="package-selector">
            <label>Package: </label>
            <select 
              value={selectedPackage} 
              onChange={handlePackageChange}
              className="package-select"
            >
              {Array.from({ length: 7 }, (_, i) => (
                <option key={i + 1} value={i + 1}>U{i + 1}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="levels-grid">
          {levelCounts.map((count, index) => (
            <div 
              key={index + 1} 
              className={`level-card ${activeLevel === index + 1 ? 'active' : ''} ${parseInt(count) > 0 ? 'has-members' : ''}`}
              onClick={() => {
                if (index + 1 === 1) {
                  handleLevelClick(index + 1);
                } else {
                  loadLevelUsers(index + 1);
                  setActiveLevel(index + 1);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="level-number">Level {index + 1}</div>
              <div className="level-count">{count}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Users in Selected Level */}
      {activeLevel === 1 && !showLevelUsers && (
        <section className="level-users-section">
          <h3>Level {activeLevel} Members ({referrals.length})</h3>
          
          {referrals.length > 0 ? (
            <>
              <div className="users-grid">
                {currentReferrals.map((referral) => (
                  <div key={referral.id} className="user-card">
                    <div className="user-info">
                      <div className="user-header">
                        <div className="user-id">ID: {referral.id}</div>
                        <div className={`status-badge ${referral.isActive ? 'active' : 'inactive'}`}>
                          {referral.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      <div className="user-address">{formatAddress(referral.account)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`pagination-btn ${pageNumber === currentPage ? 'active' : ''}`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-referrals">
              <div className="no-data-icon">👥</div>
              <h4>No Direct Referrals Yet</h4>
              <p>Share your referral link to start building your team and earn commissions!</p>
            </div>
          )}
        </section>
      )}

      {/* Matrix Level Users */}
      {showLevelUsers && (
        <section className="level-users-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>Level {selectedLevelForUsers} Matrix Members ({levelUsers.length})</h3>
            <button 
              onClick={() => setShowLevelUsers(false)}
              style={{
                background: '#64b5f6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Back to Levels
            </button>
          </div>
          
          {loadingLevelUsers ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading level users...</p>
            </div>
          ) : levelUsers.length > 0 ? (
            <>
              <div className="users-grid">
                {currentLevelUsers.map((user, index) => (
                  <div key={`${user.id}-${index}`} className="user-card">
                    <div className="user-info">
                      <div className="user-id">ID: {user.id}</div>
                      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                        Matrix Level {selectedLevelForUsers}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {levelUsersTotalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => setLevelUsersPage(levelUsersPage - 1)}
                    disabled={levelUsersPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: levelUsersTotalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setLevelUsersPage(page)}
                      className={`pagination-btn ${page === levelUsersPage ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button 
                    onClick={() => setLevelUsersPage(levelUsersPage + 1)}
                    disabled={levelUsersPage === levelUsersTotalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-referrals">
              <p>No members found in Level {selectedLevelForUsers}</p>
            </div>
          )}
        </section>
      )}

      {/* Matrix Tree View */}
      <section className="matrix-section">
        <MatrixTree 
          key={`${userId}-${selectedPackage}`}
          userId={userId}
          packageId={selectedPackage}
          matrixData={matrixData}
          network={network}
          provider={provider}
          signer={signer}
          onPackageChange={(newPackage) => {
            setSelectedPackage(newPackage);
            setShowLevelUsers(false);
            hasLoadedTeamRef.current = false; // Force reload
          }}
        />
      </section>

      {/* Activities Section */}
      <section className="activities-section">
        <h3>Recent Activities</h3>
        <div className="activities-container">
          {activities.length > 0 ? (
            <div className="activities-list">
              {activities.slice(0, 10).map((activity, index) => (
                <div key={`${activity.id}-${index}`} className="activity-item">
                  <div className="activity-icon">
                    {getActivityIcon(activity.activityType)}
                  </div>
                  <div className="activity-details">
                    <div className="activity-title">
                      {getActivityTypeLabel(activity.activityType)}
                      {activity.packageId && activity.packageId !== '0' && (
                        <span style={{ color: '#64b5f6', marginLeft: '8px' }}>
                          Package U{activity.packageId}
                        </span>
                      )}
                    </div>
                    <div className="activity-time">
                      {new Date(parseInt(activity.timestamp) * 1000).toLocaleString()}
                    </div>
                    {activity.fromId && activity.fromId !== '0' && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        From User: {activity.fromId}
                      </div>
                    )}
                  </div>
                  <div className="activity-amount">
                    {parseFloat(activity.amount).toFixed(2)} USDT
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-activities">
              <p>No activities recorded yet.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Team;